import React, { FC, useEffect, useState, ChangeEvent } from 'react';
import {
  Switch,
  FormControlLabel,
  Typography,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  SelectChangeEvent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { omit } from 'lodash';
import { Button, ButtonVariant } from 'components/common/buttons/Button';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import {
  CentsAssistInfo,
  WelcomeMessages,
  EscalationPhoneNumbers,
} from 'api/assist/getCentsAssistInfo';
import {
  AGENT_TYPE_LABELS,
  AgentType,
  AgentTypeMode,
  WritableAgentType,
} from 'constants/assist/agentType';
import { E164_PHONE_NUMBER_REGEX } from 'constants/assist/phoneNumbers';
import styles from '../assist/EditCentsAssistConfigs.module.scss';
import { useAddCentsAssistInfo } from './mutations/useAddCentsAssistInfo';
import { useEditCentsAssistInfo } from './mutations/useEditCentsAssistInfo';
import { useCentsAssistEndpoint } from './queries/useCentsAssistEndpoint';

/** `agentId` is nullable on the wire but always a string in state, so the input stays controlled. */
type AssistInfoState = Omit<CentsAssistInfo, 'agentId'> & { agentId: string };

const agentTypeModeOf = (agentType: AgentType | null): AgentTypeMode =>
  agentType === AgentType.CUSTOM ? AgentTypeMode.CUSTOM : AgentTypeMode.AUTOMATIC;

const derivedAgentTypeLabel = (agentType: AgentType | null): string =>
  agentType && agentType !== AgentType.CUSTOM
    ? AGENT_TYPE_LABELS[agentType]
    : 'Will be derived when you save';

const DEFAULT_WELCOME_MESSAGES: WelcomeMessages = {
  advancedConfig: false,
  default: null,
  afterHours: null,
  assistOff: null,
};

const DEFAULT_ESCALATION_PHONE_NUMBERS: EscalationPhoneNumbers = {
  advancedConfig: false,
  default: null,
  secondary: null,
  afterHours: null,
  assistOff: null,
};

const emptyToNull = (value: string | null): string | null => (value && value.trim() ? value : null);

export const EditCentsAssistConfigs: FC<{ businessId: number }> = ({ businessId }) => {
  const { data: centsAssistInfo, isLoading } = useCentsAssistEndpoint(businessId);
  const { mutateAsync: onAddCentsAssistInfo } = useAddCentsAssistInfo();
  const { mutateAsync: onEditCentsAssistInfo } = useEditCentsAssistInfo();

  const [assistInfo, setAssistInfo] = useState<AssistInfoState>({
    welcomeMessages: DEFAULT_WELCOME_MESSAGES,
    hoursLocationMessage: '',
    servicesPricingMessage: '',
    escalationProtocol: '',
    upsellProtocol: '',
    faqs: '',
    escalationPhoneNumbers: DEFAULT_ESCALATION_PHONE_NUMBERS,
    businessPhoneNumber: '',
    transactionalPhoneNumber: '',
    offersPud: false,
    sendOrderLinkViaSms: false,
    enableCentsSupport: false,
    bypassCallTransferWithEscalation: false,
    isLocked: false,
    agentId: '',
    agentType: null,
    supportsCardBalance: false,
  });

  const [errors, setErrors] = useState({
    escalationPhoneNumber: '',
    businessPhoneNumber: '',
    transactionalPhoneNumber: '',
    agentId: '',
  });

  const [isHandBackConfirmOpen, setIsHandBackConfirmOpen] = useState(false);

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    if (phoneNumber === 'N/A') return true;
    return E164_PHONE_NUMBER_REGEX.test(phoneNumber);
  };

  const validateTwilioPhoneNumber = (phoneNumber: string): boolean => {
    return E164_PHONE_NUMBER_REGEX.test(phoneNumber);
  };

  useEffect(() => {
    if (centsAssistInfo) {
      setAssistInfo({
        welcomeMessages: centsAssistInfo.welcomeMessages ?? DEFAULT_WELCOME_MESSAGES,
        hoursLocationMessage: centsAssistInfo.hoursLocationMessage || '',
        servicesPricingMessage: centsAssistInfo.servicesPricingMessage || '',
        escalationProtocol: centsAssistInfo.escalationProtocol || '',
        upsellProtocol: centsAssistInfo.upsellProtocol || '',
        faqs: centsAssistInfo.faqs || '',
        escalationPhoneNumbers:
          centsAssistInfo.escalationPhoneNumbers ?? DEFAULT_ESCALATION_PHONE_NUMBERS,
        transactionalPhoneNumber: centsAssistInfo.transactionalPhoneNumber || '',
        businessPhoneNumber: centsAssistInfo.businessPhoneNumber || '',
        offersPud: centsAssistInfo.offersPud || false,
        sendOrderLinkViaSms: centsAssistInfo.sendOrderLinkViaSms || false,
        enableCentsSupport: centsAssistInfo.enableCentsSupport || false,
        bypassCallTransferWithEscalation: centsAssistInfo.bypassCallTransferWithEscalation || false,
        isLocked: centsAssistInfo.isLocked || false,
        supportsCardBalance: centsAssistInfo.supportsCardBalance || false,
        agentId: centsAssistInfo.agentId || '',
        // GROW-1244: this used to be inferred from `agentId` against the legacy shared agent ids,
        // so turning Assist off for locations never changed what this screen showed.
        agentType: centsAssistInfo.agentType ?? null,
      });
    }
  }, [centsAssistInfo]);

  const handleInputChange =
    (fieldName: keyof CentsAssistInfo) => (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setAssistInfo((prev) => ({ ...prev, [fieldName]: value }));

      if (fieldName === 'transactionalPhoneNumber') {
        if (!validatePhoneNumber(value) && value !== '') {
          setErrors((prev) => ({
            ...prev,
            transactionalPhoneNumber: 'Phone number must be in format: +19999999999',
          }));
        } else {
          setErrors((prev) => ({ ...prev, transactionalPhoneNumber: '' }));
        }
      }

      if (fieldName === 'agentId' && value.trim()) {
        setErrors((prev) => ({ ...prev, agentId: '' }));
      }

      if (fieldName === 'businessPhoneNumber') {
        if (!validateTwilioPhoneNumber(value) && value !== '') {
          setErrors((prev) => ({
            ...prev,
            businessPhoneNumber: 'Phone number must be in format: +19999999999',
          }));
        } else {
          setErrors((prev) => ({ ...prev, businessPhoneNumber: '' }));
        }
      }
    };

  const handleWelcomeMessageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAssistInfo((prev) => ({
      ...prev,
      welcomeMessages: { ...prev.welcomeMessages, default: value },
    }));
  };

  const handleEscalationNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAssistInfo((prev) => ({
      ...prev,
      escalationPhoneNumbers: { ...prev.escalationPhoneNumbers, default: value },
    }));

    if (!validatePhoneNumber(value) && value !== '') {
      setErrors((prev) => ({
        ...prev,
        escalationPhoneNumber: 'Phone number must be in format: +19999999999 or N/A',
      }));
    } else {
      setErrors((prev) => ({ ...prev, escalationPhoneNumber: '' }));
    }
  };

  const handleCheckboxChange = (fieldName: string) => (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setAssistInfo((prev) => {
      // If offersPud is being turned off, also turn off sendOrderLinkViaSms
      if (fieldName === 'offersPud' && !newValue) {
        return { ...prev, [fieldName]: newValue, sendOrderLinkViaSms: false };
      }
      // if bypassCallTransferWithEscalation is being turned on, disable enableCentsSupport
      if (fieldName === 'bypassCallTransferWithEscalation' && newValue) {
        return { ...prev, [fieldName]: newValue, enableCentsSupport: false };
      }

      return { ...prev, [fieldName]: newValue };
    });
  };

  const applyAgentTypeMode = (mode: AgentTypeMode) =>
    setAssistInfo((prev) => ({
      ...prev,
      agentType: mode === AgentTypeMode.CUSTOM ? AgentType.CUSTOM : null,
      // Dropping the pointer gives the business a fresh managed agent instead of regenerating the
      // hand-built one. The backend enforces this too, whatever we send.
      agentId: mode === AgentTypeMode.AUTOMATIC ? '' : prev.agentId,
    }));

  /**
   * Leaving CUSTOM is destructive for an agent already bound to a phone number — Cents takes its
   * conversation flow over — so it is confirmed first.
   */
  const handleAgentTypeModeChange = (e: SelectChangeEvent<AgentTypeMode>) => {
    const mode = e.target.value as AgentTypeMode;

    if (mode === AgentTypeMode.AUTOMATIC && assistInfo.agentType === AgentType.CUSTOM) {
      setIsHandBackConfirmOpen(true);

      return;
    }

    applyAgentTypeMode(mode);
  };

  const handleConfirmHandBack = () => {
    applyAgentTypeMode(AgentTypeMode.AUTOMATIC);
    setIsHandBackConfirmOpen(false);
  };

  const handleSaveAssistInfoClick = async () => {
    const { welcomeMessages, escalationPhoneNumbers } = assistInfo;

    // Never send SINGLE_STORE / MULTI_STORE: derived values, the API 422s them.
    const agentType: WritableAgentType =
      assistInfo.agentType === AgentType.CUSTOM ? AgentType.CUSTOM : null;

    // The API rejects this too; failing here keeps the message next to the field.
    if (agentType === AgentType.CUSTOM && !assistInfo.agentId.trim()) {
      setErrors((prev) => ({
        ...prev,
        agentId: 'Agent ID is required for a Custom agent',
      }));

      return;
    }

    setErrors((prev) => ({ ...prev, agentId: '' }));

    const payload = {
      businessId,
      ...omit(assistInfo, [
        'agentType',
        'id',
        'welcomeMessage',
        'escalationPhoneNumber',
        'secondaryEscalationPhoneNumber',
      ]),
      agentType,
      welcomeMessages: { ...welcomeMessages, default: emptyToNull(welcomeMessages.default) },
      escalationPhoneNumbers: {
        ...escalationPhoneNumbers,
        default: emptyToNull(escalationPhoneNumbers.default),
      },
    };

    const apiCall = centsAssistInfo ? onEditCentsAssistInfo : onAddCentsAssistInfo;

    const { data } = await apiCall(payload);

    setAssistInfo((prev) => ({
      ...prev,
      ...data,
      // The API returns null once a phone number is bound to the agent; state keeps it a string.
      agentId: data.agentId ?? '',
    }));
  };

  return (
    <div className={styles.editAssistConfigsFormContainer}>
      <h1 className={styles.editAssistConfigsFormContainerHeader}>Edit Cents Assist Configs</h1>
      {isLoading ? (
        <CentsLoader />
      ) : (
        <div className={styles.editAssistConfigsFormContainerControls}>
          <div className={styles.lockSection}>
            <FormControlLabel
              control={
                <Switch
                  checked={assistInfo.isLocked}
                  onChange={handleCheckboxChange('isLocked')}
                  color="error"
                />
              }
              label="Lock Customer Configs"
            />
          </div>
          <FormControl style={{ margin: '0 0 10px 10px' }} fullWidth>
            <InputLabel>Agent Type</InputLabel>
            <Select
              value={agentTypeModeOf(assistInfo.agentType)}
              onChange={handleAgentTypeModeChange}
              label="Agent Type"
              disabled={assistInfo.isLocked}
            >
              <MenuItem value={AgentTypeMode.AUTOMATIC}>Automatic — Single / Multi Store</MenuItem>
              <MenuItem value={AgentTypeMode.CUSTOM}>
                {AGENT_TYPE_LABELS[AgentType.CUSTOM]}
              </MenuItem>
            </Select>
            <FormHelperText>
              {assistInfo.agentType === AgentType.CUSTOM
                ? 'Hand-built agent — Cents will not regenerate its conversation flow.'
                : `Derived from the locations with Assist enabled, not set by hand — currently ${derivedAgentTypeLabel(
                    assistInfo.agentType,
                  )}.`}
            </FormHelperText>
          </FormControl>
          {assistInfo.agentType === AgentType.CUSTOM && (
            <TextField
              type="text"
              variant="outlined"
              color="secondary"
              label="Agent ID"
              onChange={handleInputChange('agentId')}
              value={assistInfo.agentId}
              fullWidth
              disabled={assistInfo.isLocked}
              error={!!errors.agentId}
              helperText={errors.agentId || 'Enter custom Agent ID'}
            />
          )}
          <TextField
            type="text"
            multiline
            variant="outlined"
            color="secondary"
            label="Welcome Message"
            onChange={handleWelcomeMessageChange}
            value={assistInfo.welcomeMessages.default ?? ''}
            fullWidth
            disabled={assistInfo.isLocked}
          />
          <TextField
            type="text"
            multiline
            variant="outlined"
            color="secondary"
            label="Hours / Locations Message"
            onChange={handleInputChange('hoursLocationMessage')}
            value={assistInfo.hoursLocationMessage}
            fullWidth
            disabled={assistInfo.isLocked}
          />
          <TextField
            type="text"
            multiline
            variant="outlined"
            color="secondary"
            label="Services / Pricing Message"
            onChange={handleInputChange('servicesPricingMessage')}
            value={assistInfo.servicesPricingMessage}
            fullWidth
            disabled={assistInfo.isLocked}
          />
          <TextField
            type="text"
            multiline
            variant="outlined"
            color="secondary"
            label="Upsell Protocol"
            onChange={handleInputChange('upsellProtocol')}
            value={assistInfo.upsellProtocol}
            fullWidth
            disabled={assistInfo.isLocked}
          />
          <TextField
            type="text"
            multiline
            variant="outlined"
            color="secondary"
            label="FAQs"
            onChange={handleInputChange('faqs')}
            value={assistInfo.faqs}
            fullWidth
            disabled={assistInfo.isLocked}
          />
          <TextField
            type="text"
            variant="outlined"
            color="secondary"
            label="Default Transfer Number"
            onChange={handleEscalationNumberChange}
            value={assistInfo.escalationPhoneNumbers.default ?? ''}
            fullWidth
            disabled={assistInfo.isLocked}
            error={!!errors.escalationPhoneNumber}
            helperText={errors.escalationPhoneNumber || 'Format: +19999999999 or N/A'}
          />
          <TextField
            type="text"
            multiline
            variant="outlined"
            color="secondary"
            label="Conditional Escalation Protocol"
            onChange={handleInputChange('escalationProtocol')}
            value={assistInfo.escalationProtocol}
            fullWidth
            helperText="Format: If the user is calling about the _______ location, use +19999999999. If the user asks for a manager, use +19999999999."
            disabled={assistInfo.isLocked}
          />
          <TextField
            type="text"
            variant="outlined"
            color="secondary"
            label="Marketing Phone Number"
            onChange={handleInputChange('businessPhoneNumber')}
            value={assistInfo.businessPhoneNumber}
            fullWidth
            disabled={assistInfo.isLocked}
            error={!!errors.businessPhoneNumber}
            helperText={errors.businessPhoneNumber || 'Format: +19999999999'}
          />

          <TextField
            type="text"
            variant="outlined"
            color="secondary"
            label="Transactional Phone Number"
            onChange={handleInputChange('transactionalPhoneNumber')}
            value={assistInfo.transactionalPhoneNumber}
            fullWidth
            disabled={assistInfo.isLocked}
            error={!!errors.transactionalPhoneNumber}
            helperText={errors.transactionalPhoneNumber || 'Format: +19999999999'}
          />

          <div className={styles.featuresSection}>
            <Typography variant="h6" className={styles.featuresHeader}>
              Features
            </Typography>
            <div className={styles.featuresContent}>
              <FormControlLabel
                control={
                  <Switch
                    checked={assistInfo.offersPud}
                    onChange={handleCheckboxChange('offersPud')}
                    color="secondary"
                    disabled={assistInfo.isLocked}
                  />
                }
                label="Offers PUD"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={assistInfo.sendOrderLinkViaSms}
                    onChange={handleCheckboxChange('sendOrderLinkViaSms')}
                    color="secondary"
                    disabled={!assistInfo.offersPud || assistInfo.isLocked}
                  />
                }
                label="Send Order Link via SMS before Transferring"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={assistInfo.enableCentsSupport}
                    onChange={handleCheckboxChange('enableCentsSupport')}
                    color="secondary"
                    disabled={assistInfo.isLocked || assistInfo.bypassCallTransferWithEscalation}
                  />
                }
                label="Enable Cents Support"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={assistInfo.bypassCallTransferWithEscalation}
                    onChange={handleCheckboxChange('bypassCallTransferWithEscalation')}
                    color="secondary"
                    disabled={assistInfo.isLocked}
                  />
                }
                label="Bypass Call Transfer with Escalation"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={assistInfo.supportsCardBalance}
                    onChange={handleCheckboxChange('supportsCardBalance')}
                    color="secondary"
                    disabled={assistInfo.isLocked}
                  />
                }
                label="Share the card balance"
              />
            </div>
          </div>
          <Button
            style={{ width: '50%', alignSelf: 'center', marginLeft: '10px' }}
            type={ButtonVariant.CONTAINED}
            onClick={handleSaveAssistInfoClick}
          >
            Save
          </Button>
          <Dialog
            open={isHandBackConfirmOpen}
            onClose={() => setIsHandBackConfirmOpen(false)}
            aria-labelledby="hand-back-confirm-title"
          >
            <DialogTitle id="hand-back-confirm-title">Hand this agent back to Cents?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Cents will manage the agent type again, deriving it from the locations that have
                Assist enabled, and the stored Agent ID will be cleared. If a phone number is
                already bound to the hand-built agent, its conversation flow will be replaced with
                the generated one on the next sync.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button type={ButtonVariant.OUTLINED} onClick={() => setIsHandBackConfirmOpen(false)}>
                Cancel
              </Button>
              <Button type={ButtonVariant.CONTAINED} onClick={handleConfirmHandBack}>
                Hand back to Cents
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </div>
  );
};
